import { useId } from "react";
import type { HTMLAttributes, ReactNode } from "react";
import { AlertCircle } from "lucide-react";
import { cn } from "../lib/cn";
import { Label } from "./Label";

/** Props que o Field entrega ao controle para o vínculo ARIA ficar correto. */
export interface FieldControlProps {
  id: string;
  "aria-describedby": string | undefined;
  "aria-invalid": true | undefined;
  "aria-required": true | undefined;
}

export interface FieldProps extends Omit<HTMLAttributes<HTMLDivElement>, "children"> {
  label: ReactNode;
  /** Texto de ajuda. Some da tela quando há erro (o erro assume o lugar). */
  hint?: ReactNode;
  /** Alias de `hint`, com o nome usado no Claude Design. */
  help?: ReactNode;
  /** Mensagem de erro. Presença dela é o que marca o campo como inválido. */
  error?: ReactNode;
  required?: boolean;
  disabled?: boolean;
  /** Use o `id` gerado aqui em vez de inventar um. */
  htmlFor?: string;
  /**
   * Render-prop: recebe id + aria-* já resolvidos.
   * Também aceita um node solto, mas aí o vínculo ARIA é por sua conta.
   */
  children: ReactNode | ((control: FieldControlProps) => ReactNode);
}

/**
 * Label + controle + (hint | erro), com `aria-describedby` correto.
 * Não decide validação: só exibe o que o chamador passou.
 */
export function Field({
  label,
  hint: hintProp,
  help,
  error,
  required = false,
  disabled = false,
  htmlFor,
  className,
  children,
  ...props
}: FieldProps) {
  const hint = hintProp ?? help;
  const generatedId = useId();
  const id = htmlFor ?? `field-${generatedId}`;
  const hintId = `${id}-hint`;
  const errorId = `${id}-error`;
  const hasError = Boolean(error);

  const describedBy =
    [hasError ? errorId : null, hint && !hasError ? hintId : null]
      .filter(Boolean)
      .join(" ") || undefined;

  const control: FieldControlProps = {
    id,
    "aria-describedby": describedBy,
    "aria-invalid": hasError ? true : undefined,
    "aria-required": required ? true : undefined,
  };

  return (
    <div className={cn("flex flex-col gap-1.5", className)} {...props}>
      <Label htmlFor={id} required={required} disabled={disabled}>
        {label}
      </Label>

      {typeof children === "function" ? children(control) : children}

      {hasError ? (
        <p
          id={errorId}
          className="flex items-start gap-1.5 text-xs text-danger"
        >
          <AlertCircle aria-hidden="true" className="mt-px size-3.5 shrink-0" />
          <span>{error}</span>
        </p>
      ) : hint ? (
        <p id={hintId} className="text-xs text-subtle-foreground">
          {hint}
        </p>
      ) : null}
    </div>
  );
}
